import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { CheckCircle2, Circle } from "lucide-mui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  type ContactDataPoint,
  type DataProvider,
  type DataProviderConfigField,
  type DataProviderPrice,
  type DataProviderTestResult,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import AccordionCard from "../../../components/AccordionCard.tsx";
import DataProviderLogo from "../../../components/DataProviderLogo.tsx";
import { AdvancedFold } from "../../../components/FoldList.tsx";
import SecretCredentialForm from "../../../components/SecretCredentialForm.tsx";
import type { Status } from "../../../components/StatusIcon.tsx";
import FormattedDate from "../../../components/FormattedDate";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { formatMoney, formatNumber } from "../../../utils/format.ts";
import { withoutWhitespace } from "../../../utils/secrets.ts";
import PageHeader from "../../../components/PageHeader.tsx";

const DATA_POINT_LABEL: Record<ContactDataPoint, string> = {
  person_profile: "Person profile",
  work_email: "Work email",
  mobile_phone: "Phone number",
  company_profile: "Company lookup",
};

function ChecklistItem({ done, children }: { done: boolean; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {done ? (
        <CheckCircle2 fontSize="small" color="success" />
      ) : (
        <Circle fontSize="small" sx={{ color: "text.disabled" }} />
      )}
      <Typography variant="body2" sx={{ color: done ? "text.primary" : "text.secondary" }}>
        {children}
      </Typography>
    </Stack>
  );
}

/**
 * What still stands between this provider and a sale, from saved state only:
 * the key, the switch, and a credit pack to price each data point it can buy.
 * A partner provider (`billed: false`) is free, so only the key and switch.
 */
function SetupChecklist({ provider }: { provider: DataProvider }) {
  const prices = useQuery({
    queryKey: keys.dataProvider.prices(provider.provider),
    queryFn: () => api.getDataProviderPrices(provider.provider),
    enabled: provider.billed,
  });
  const defaults = new Map(
    (prices.data ?? [])
      .filter((p) => p.organization_id === null)
      .map((p) => [p.data_point, p] as const),
  );
  const unavailable = provider.supported_data_points.filter(
    (dp) => !provider.available_data_points.includes(dp),
  );

  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Setup
      </Typography>
      <ChecklistItem done={provider.secret_stored}>API key stored</ChecklistItem>
      <ChecklistItem done={provider.enabled}>Enabled for purchases</ChecklistItem>
      {provider.billed ? (
        <BilledChecklist provider={provider} defaults={defaults} />
      ) : (
        <>
          {provider.available_data_points.map((dp) => (
            <ChecklistItem key={dp} done>
              {DATA_POINT_LABEL[dp] ?? dp}: free
            </ChecklistItem>
          ))}
          <Typography variant="caption" sx={{ color: "text.secondary", pt: 0.5 }}>
            A partner: free to every organization, with no credit pack, prices or spend limits.
            Usage per client is on the <RouterLink to="/platform">platform dashboard</RouterLink>.
          </Typography>
        </>
      )}
      {unavailable.map((dp) => (
        <ChecklistItem key={dp} done={false}>
          {DATA_POINT_LABEL[dp] ?? dp}: off — {provider.unavailable_hints[dp] ?? "not with these settings"}
        </ChecklistItem>
      ))}
    </Stack>
  );
}

function BilledChecklist({
  provider,
  defaults,
}: {
  provider: DataProvider;
  defaults: Map<string, DataProviderPrice>;
}) {
  return (
    <>
      <ChecklistItem done={defaults.size > 0}>
        {defaults.size > 0 ? "Credit pack entered" : "Credit pack: enter it under Pricing below"}
      </ChecklistItem>
      {provider.available_data_points.map((dp) => {
        const price = defaults.get(dp);
        return (
          <ChecklistItem key={dp} done={price !== undefined}>
            {DATA_POINT_LABEL[dp] ?? dp}:{" "}
            {price
              ? `${formatMoney(price.unit_price, price.currency)} (${price.credits} credits)`
              : "not priced yet"}
          </ChecklistItem>
        );
      })}
      <Typography variant="caption" sx={{ color: "text.secondary", pt: 0.5 }}>
        Each organization also needs a monthly spend limit, set by its admin under{" "}
        <RouterLink to="/organization/contact-data">Contact data</RouterLink>.
      </Typography>
    </>
  );
}

function SettingsForm({ provider }: { provider: DataProvider }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(provider.enabled);
  const [secret, setSecret] = useState("");
  const [config, setConfig] = useState<Record<string, string>>(provider.config);
  const [testResult, setTestResult] = useState<DataProviderTestResult | null>(null);
  const secretField = provider.config_fields.find((f) => f.secret);
  const advancedFields = provider.config_fields.filter((f) => f.advanced && !f.secret);
  // Saved corrections: named while folded, and the fold starts open so one is never hidden.
  const overridden = advancedFields.filter((f) => provider.config[f.name]);

  // Every DataProvider query: the credit settings saved here re-price data points.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.dataProvider.all });

  const save = useMutation({
    mutationFn: () =>
      api.updateDataProvider(provider.provider, {
        enabled,
        secret: withoutWhitespace(secret) || null,
        config,
      }),
    onSuccess: () => {
      setSecret("");
      setTestResult(null);
      invalidate();
      toast.success(`${provider.name} saved`);
    },
  });
  const clear = useMutation({
    mutationFn: () => api.clearDataProviderSecret(provider.provider),
    onSuccess: () => {
      setEnabled(false);
      setTestResult(null);
      invalidate();
      toast.success(`${provider.name} key cleared`);
    },
  });
  const test = useMutation({
    mutationFn: () => api.testDataProvider(provider.provider),
    onSuccess: setTestResult,
  });

  const renderField = (field: DataProviderConfigField) => (
    <TextField
      key={field.name}
      label={field.label}
      value={config[field.name] ?? ""}
      onChange={(e) => setConfig((c) => ({ ...c, [field.name]: e.target.value }))}
      placeholder={field.default}
      helperText={field.help_text || (field.default ? `Default: ${field.default}` : " ")}
      fullWidth
      size="small"
    />
  );

  return (
    <Stack spacing={2}>
      <SecretCredentialForm
        name={secretField?.label ?? "API key"}
        stored={provider.secret_stored}
        hint={provider.secret_hint}
        updatedAt={provider.updated_at}
        updatedByEmail={provider.updated_by_email}
        encryptionConfigured={provider.encryption_configured}
        encryptionWarning="saving a key is refused rather than storing it in clear text."
        value={secret}
        onChange={setSecret}
        fieldLabel={secretField?.label ?? "API key"}
        placeholder={provider.secret_stored ? `Stored ••••${provider.secret_hint}` : ""}
        helperText={
          provider.secret_stored
            ? "Write-only. Leave blank to keep the stored key."
            : secretField?.help_text || "Write-only — never shown again."
        }
        onTest={() => test.mutate()}
        testing={test.isPending}
        testDisabled={!provider.secret_stored}
        testLabel="Test connection"
        testResult={
          testResult && {
            ok: testResult.ok,
            detail: (
              <>
                {testResult.detail}
                {testResult.ok && testResult.info.details?.length ? (
                  <>
                    {" "}
                    {testResult.info.details
                      .map((d) => `${d.label}: ${typeof d.value === "number" ? formatNumber(d.value) : d.value}`)
                      .join(" · ")}
                    .
                  </>
                ) : (
                  testResult.ok &&
                  testResult.info.credit_balance != null && (
                    <> Credit balance: {String(testResult.info.credit_balance)}.</>
                  )
                )}
                {testResult.ok && Array.isArray(testResult.info.available_data_points) && (
                  <> Can buy: {(testResult.info.available_data_points as string[]).join(", ")}.</>
                )}
              </>
            ),
          }
        }
        onClear={() => clear.mutateAsync()}
        clearLabel="Clear key"
        clearConfirm={{
          title: `Clear ${provider.name} key`,
          body: (
            <>
              Clear the stored API key? {provider.name} is disabled and nothing can be bought from
              it until a new key is saved.
            </>
          ),
        }}
      />
      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label="Enabled for purchases"
      />
      {provider.config_fields.filter((f) => !f.advanced && !f.secret).map(renderField)}
      {advancedFields.length > 0 && (
        <AdvancedFold
          summary={
            overridden.length ? `${overridden.map((f) => f.label).join(", ")} changed` : "Defaults"
          }
        >
          {/* Room for the outlined fields' floating labels inside the fold. */}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Corrections for parts of the provider's API that are not publicly documented. Leave
              blank to use the defaults; only change one if purchases come back empty or fail.
            </Typography>
            {advancedFields.map(renderField)}
          </Stack>
        </AdvancedFold>
      )}
      <Box>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!!withoutWhitespace(secret) && !provider.encryption_configured}
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
}

/** The markup typed as text, or null when it is not a usable number. */
function parseMarkup(value: string): number | null {
  const n = Number(value);
  return value.trim() !== "" && Number.isFinite(n) && n >= 0 && n < 1000 ? n : null;
}

function CreditPricing({ provider }: { provider: DataProvider }) {
  const queryClient = useQueryClient();
  const [packPrice, setPackPrice] = useState("");
  const [packCredits, setPackCredits] = useState("");
  const [markup, setMarkup] = useState("1");
  const [packFrom, setPackFrom] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgMarkup, setOrgMarkup] = useState("");
  const [orgFrom, setOrgFrom] = useState("");

  const prices = useQuery({
    queryKey: keys.dataProvider.prices(provider.provider),
    queryFn: () => api.getDataProviderPrices(provider.provider),
  });
  const rates = useQuery({
    queryKey: keys.dataProvider.creditRates(provider.provider),
    queryFn: () => api.getDataProviderCreditRates(provider.provider),
  });
  const orgs = useQuery({
    queryKey: keys.organization.list(),
    queryFn: () => api.getOrganizations(),
  });

  const onAdded = (message: string) => {
    queryClient.invalidateQueries({ queryKey: keys.dataProvider.prices(provider.provider) });
    queryClient.invalidateQueries({ queryKey: keys.dataProvider.creditRates(provider.provider) });
    toast.success(message);
  };
  const toIso = (local: string) => (local ? new Date(local).toISOString() : null);

  const addPack = useMutation({
    mutationFn: () =>
      api.addDataProviderCreditRate(provider.provider, {
        package_price: Number(packPrice),
        package_credits: Number(packCredits),
        markup: parseMarkup(markup) ?? 1,
        effective_from: toIso(packFrom),
      }),
    onSuccess: () => {
      setPackPrice("");
      setPackCredits("");
      setPackFrom("");
      onAdded("Credit pack set");
    },
  });
  const addOrgMarkup = useMutation({
    mutationFn: () =>
      api.addDataProviderCreditRate(provider.provider, {
        markup: parseMarkup(orgMarkup) ?? 1,
        organization_id: orgId,
        effective_from: toIso(orgFrom),
      }),
    onSuccess: () => {
      setOrgMarkup("");
      setOrgFrom("");
      onAdded("Markup set");
    },
  });

  const credits = Number(packCredits);
  const perCredit =
    Number(packPrice) > 0 && Number.isInteger(credits) && credits > 0
      ? Number(packPrice) / credits
      : null;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Pricing
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {provider.name} is paid in credits, so each data point is billed at the credits it costs
        (set in the settings above) × what one credit costs us × the markup. An organization
        markup overrides the default for that organization. Rates are never edited: adding one
        supersedes the previous from its start date, and every charge keeps the price it was
        bought at.
      </Typography>

      {prices.isLoading ? (
        <CenteredSpinner size={24} />
      ) : prices.data?.length ? (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Data point</TableCell>
              <TableCell>Applies to</TableCell>
              <TableCell align="right">Credits</TableCell>
              <TableCell align="right">Markup</TableCell>
              <TableCell align="right">Price now</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prices.data.map((p) => (
              <TableRow key={`${p.organization_id ?? "default"}-${p.data_point}`}>
                <TableCell>{DATA_POINT_LABEL[p.data_point] ?? p.data_point}</TableCell>
                <TableCell>{p.organization_name ?? "All organizations"}</TableCell>
                <TableCell align="right">{p.credits}</TableCell>
                <TableCell align="right">{p.markup}×</TableCell>
                <TableCell align="right">{formatMoney(p.unit_price, p.currency)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>
      ) : (
        <Alert severity="info">
          No credit pack yet — nothing can be bought until one is entered below.
        </Alert>
      )}

      <Typography variant="subtitle2">Credit pack</Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="Pack price (USD)"
          type="number"
          value={packPrice}
          onChange={(e) => setPackPrice(e.target.value)}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          label="Credits in pack"
          type="number"
          value={packCredits}
          onChange={(e) => setPackCredits(e.target.value)}
          helperText={perCredit ? `${formatMoney(perCredit)} per credit` : " "}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          label="Default markup (×)"
          type="number"
          value={markup}
          onChange={(e) => setMarkup(e.target.value)}
          helperText="1 = at cost"
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          label="From"
          type="datetime-local"
          value={packFrom}
          onChange={(e) => setPackFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="Empty: now"
        />
        <Button
          variant="outlined"
          onClick={() => addPack.mutate()}
          loading={addPack.isPending}
          disabled={perCredit === null || parseMarkup(markup) === null}
        >
          Set pack
        </Button>
      </Stack>

      <Typography variant="subtitle2">Organization markup</Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <TextField
          select
          size="small"
          label="Organization"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {(orgs.data ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Markup (×)"
          type="number"
          value={orgMarkup}
          onChange={(e) => setOrgMarkup(e.target.value)}
          sx={{ width: 130 }}
        />
        <TextField
          size="small"
          label="From"
          type="datetime-local"
          value={orgFrom}
          onChange={(e) => setOrgFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="Empty: now"
        />
        <Button
          variant="outlined"
          onClick={() => addOrgMarkup.mutate()}
          loading={addOrgMarkup.isPending}
          disabled={!orgId || parseMarkup(orgMarkup) === null}
        >
          Set markup
        </Button>
      </Stack>

      {(rates.data?.length ?? 0) > 0 && (
        <>
          <Typography variant="subtitle2">History</Typography>
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Applies to</TableCell>
                <TableCell align="right">Pack</TableCell>
                <TableCell align="right">Per credit</TableCell>
                <TableCell align="right">Markup</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Added by</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(rates.data ?? []).map((r) => (
                <TableRow key={r.id} sx={{ opacity: r.in_force ? 1 : 0.55 }}>
                  <TableCell>{r.organization_name ?? "All organizations"}</TableCell>
                  <TableCell align="right">
                    {r.package_price !== null && r.package_credits !== null
                      ? `${formatMoney(r.package_price, r.currency)} / ${formatNumber(r.package_credits)}`
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {r.credit_price !== null ? formatMoney(r.credit_price, r.currency) : "—"}
                  </TableCell>
                  <TableCell align="right">{r.markup}×</TableCell>
                  <TableCell>
                    <FormattedDate value={r.effective_from} format="datetime" />
                  </TableCell>
                  <TableCell>{r.created_by_email ?? "—"}</TableCell>
                  <TableCell>
                    {r.in_force && <Chip size="small" color="success" label="In force" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        </>
      )}
    </Stack>
  );
}

function providerStatus(provider: DataProvider): Status {
  if (!provider.secret_stored) return { kind: "attention", label: "No key" };
  if (!provider.enabled) return { kind: "off", label: "Disabled" };
  return { kind: "ok", label: "Enabled" };
}

/**
 * Developer setup of contact-data providers: the platform's own account key, the
 * provider's settings, and for a paid one the credit pack and markups
 * organizations are billed by.
 */
export default function DataProvidersPage() {
  const providers = useQuery({
    queryKey: keys.dataProvider.list(),
    queryFn: () => api.getDataProviders(),
  });
  const list = providers.data ?? [];

  return (
    <>
      <PageHeader
        title="Data providers"
        description={
          <>
            Contact-data providers, bought from the platform's own accounts: paid ones billed to
            the organization that asked, partners free. Keys are stored encrypted and used from the next request — no
            redeploy needed.
          </>
        }
      />
      {providers.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(providers.error)}
        </Alert>
      )}
      {providers.isLoading ? (
        <CenteredSpinner />
      ) : (
        <Stack spacing={1.5}>
          {list.map((provider) => {
            const status = providerStatus(provider);
            const sells = provider.available_data_points.map((dp) => DATA_POINT_LABEL[dp] ?? dp);
            return (
              <AccordionCard
                key={provider.provider}
                logo={<DataProviderLogo name={provider.name} />}
                title={provider.name}
                summary={[
                  provider.secret_stored ? `API key …${provider.secret_hint}` : "No API key stored",
                  ...(provider.billed ? [] : ["Partner — free for all organizations"]),
                  sells.length ? `Sells ${sells.join(", ").toLowerCase()}` : "Sells nothing yet",
                ].join(" · ")}
                status={status}
                defaultExpanded={status.kind === "attention" || list.length === 1}
              >
                <Stack spacing={3} divider={<Divider flexItem />}>
                  <SetupChecklist provider={provider} />
                  {/* Remounts after a save, so the form shows what was stored. */}
                  <SettingsForm key={provider.updated_at ?? ""} provider={provider} />
                  {provider.billed && <CreditPricing provider={provider} />}
                </Stack>
              </AccordionCard>
            );
          })}
        </Stack>
      )}
    </>
  );
}
